import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CallDecisionRequest,
  CallDecisionResponse,
  CreateStepCommentRequest,
  RunnerState,
  RunnerStep,
  TestRunExecution,
  TestStepComment,
  UpdateRunnerStepRequest,
} from '../models/test-run.model';

/**
 * Der Runner eines Durchlaufs (NEX-1041): Zustand lesen, Schritte fortschreiben,
 * kommentieren, abschliessen.
 *
 * <p>Eigener Service neben {@link TestRunService}: der haelt Uebersicht,
 * Anlegen und die Zwischenebene, hier liegt die Durchfuehrung eines einzelnen
 * Durchlaufs — dieselbe Trennung wie im Backend.
 */
@Injectable({ providedIn: 'root' })
export class TestlaufRunnerService {
  private readonly http = inject(HttpClient);

  private execution(projectId: string, runId: string, executionId: string): string {
    return `${environment.apiBaseUrl}/api/v1/projects/${projectId}/test-runs/${runId}/executions/${executionId}`;
  }

  getState(projectId: string, runId: string, executionId: string): Observable<RunnerState> {
    return this.http.get<RunnerState>(this.execution(projectId, runId, executionId));
  }

  /**
   * Der PATCH ist partiell: was der Aufrufer nicht mitgibt, bleibt am Schritt
   * stehen. Deshalb wandert das Request-Objekt unveraendert durch, statt hier
   * mit Leerwerten aufgefuellt zu werden.
   */
  updateStep(
    projectId: string,
    runId: string,
    executionId: string,
    stepResultId: string,
    request: UpdateRunnerStepRequest,
  ): Observable<RunnerStep> {
    return this.http.patch<RunnerStep>(
      `${this.execution(projectId, runId, executionId)}/steps/${stepResultId}`,
      request,
    );
  }

  addComment(
    projectId: string,
    runId: string,
    executionId: string,
    stepResultId: string,
    body: string,
  ): Observable<TestStepComment> {
    const request: CreateStepCommentRequest = { body };
    return this.http.post<TestStepComment>(
      `${this.execution(projectId, runId, executionId)}/steps/${stepResultId}/comments`,
      request,
    );
  }

  complete(projectId: string, runId: string, executionId: string): Observable<TestRunExecution> {
    return this.http.post<TestRunExecution>(`${this.execution(projectId, runId, executionId)}/complete`, {});
  }

  /** Entscheidung an einem offenen Modulaufruf (NEX-2277); die Antwort traegt die neuen Zeilen. */
  decideCall(
    projectId: string,
    runId: string,
    executionId: string,
    stepResultId: string,
    request: CallDecisionRequest,
  ): Observable<CallDecisionResponse> {
    return this.http.post<CallDecisionResponse>(
      `${this.execution(projectId, runId, executionId)}/calls/${stepResultId}/decision`,
      request,
    );
  }
}
